import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import { makeStyles } from "@material-ui/core/styles";
import ClearIcon from "@material-ui/icons/Clear";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import isValidDate from "date-fns/isValid";
import subDays from "date-fns/subDays";
import { Form, Formik, FormikProps } from "formik";
import React, { useEffect } from "react";
import * as Yup from "yup";
import CustomDatePicker from "../../components/Input/CustomDatePicker";
import DateRange, { DateRangePropertiesNullable, nullifyEmptyDateRange } from "./DateRange.model";

const useStyles = makeStyles({
  reset: {
    display: "flex",
    transform: "translateX(-20px)",
  },
});

type DateFormType = DateRangePropertiesNullable<Date>;

const DEFAULT_RANGE_IN_DAYS = 6;
const INITIAL_VALUES_EMPTY: DateFormType = {
  from: null,
  to: null,
};
const TODAY = new Date();

const errorMessages = {
  INVALID_DATE: "Please enter a valid date.",
  FROM_MAX: "Cannot be after end date.",
  REQUIRED: "Please provide a value.",
};

const fields: { [K: string]: keyof DateFormType } = {
  START_DATE: "from",
  END_DATE: "to",
} as const;

const labels = {
  START_DATE: "Start date",
  END_DATE: "End date",
};

const getDefaultValues = (range?: number): DateFormType => {
  return {
    from: subDays(TODAY, range ?? DEFAULT_RANGE_IN_DAYS),
    to: TODAY,
  };
};

const getBaseValidation = (isNullable: boolean) => {
  const base = Yup.date().typeError(errorMessages.INVALID_DATE).nullable(isNullable);
  return isNullable ? base : base.required(errorMessages.REQUIRED);
};

const getValidationSchema = (isNullable: boolean) =>
  Yup.object().shape({
    [fields.START_DATE]: getBaseValidation(isNullable)
      .when(fields.END_DATE, (endDate: Date, schema: Yup.DateSchema) =>
        endDate && isValidDate(endDate) ? schema.max(endDate, errorMessages.FROM_MAX) : schema,
      )
      .transform((v) => (isValidDate(v) ? v : null)),
    [fields.END_DATE]: getBaseValidation(isNullable).transform((v) => (isValidDate(v) ? v : null)),
  });

interface CustomDateRangePickerProps {
  nullable?: boolean;
  onChange: (dateRange: DateRange<Date> | null) => void;
  rangeInDays?: number;
}

const CustomDateRangePicker: React.FC<CustomDateRangePickerProps> = ({
  nullable,
  onChange: handleChange,
  rangeInDays,
}) => {
  const classes = useStyles();

  return (
    <Formik<DateFormType>
      enableReinitialize
      initialValues={nullable ? INITIAL_VALUES_EMPTY : getDefaultValues(rangeInDays)}
      onSubmit={(dateRange) => {
        const nullIfEmpty = nullifyEmptyDateRange(dateRange);
        handleChange(nullIfEmpty);
      }}
      validationSchema={getValidationSchema(!!nullable)}
    >
      {({ errors, resetForm, setFieldValue, submitForm, isValid, values }: FormikProps<DateFormType>) => {
        const handleLocalDateChange =
          (fieldName: typeof fields[keyof typeof fields]) => (date: MaterialUiPickersDate) => {
            setFieldValue(fieldName, date);
          };

        const handleReset = () => {
          resetForm();
        };

        useEffect(() => {
          if (isValid) {
            submitForm();
          }
        }, [isValid, values]);

        return (
          <Form>
            <Grid container spacing={3}>
              <Grid item sm={5} md={4} lg={3} xl={2}>
                <CustomDatePicker
                  disableFuture
                  error={!!errors[fields.START_DATE]}
                  helperText={errors[fields.START_DATE]}
                  onChange={handleLocalDateChange(fields.START_DATE)}
                  label={labels.START_DATE}
                  value={values[fields.START_DATE]}
                />
              </Grid>
              <Grid item sm={5} md={4} lg={3} xl={2}>
                <CustomDatePicker
                  disableFuture
                  error={!!errors[fields.END_DATE]}
                  helperText={errors[fields.END_DATE]}
                  onChange={handleLocalDateChange(fields.END_DATE)}
                  label={labels.END_DATE}
                  value={values[fields.END_DATE]}
                />
              </Grid>
              {nullable && (
                <Grid className={classes.reset} item xs={2}>
                  <IconButton onClick={handleReset}>
                    <ClearIcon />
                  </IconButton>
                </Grid>
              )}
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};

export default CustomDateRangePicker;
